/**
 * ShelfMapRenderer – interactive SVG renderer for warehouse shelf maps.
 *
 * Issue: #215 – CORE – Warehouse Shelf Map – Frontend SVG Renderer (Zoom/Pan/Select)
 *
 * Layout model
 * ────────────
 * Aisles are rendered as full-width horizontal bands (one per aisle label).
 * The aisle label sits in a fixed-width left column; slot cells fill the
 * remainder of the row from left to right. If a shelf has more slots than
 * fit in the available width they scroll horizontally. Multiple shelf rows
 * within the same aisle stack vertically inside the band.
 *
 * Features:
 *   - Full-width rows: each aisle occupies its own row spanning the container.
 *   - Aisle label column on the left (fixed 48 px).
 *   - Cells fill right then wrap per shelf.
 *   - Wheel-scroll pan; no zoom scaling needed with this layout.
 *   - Click a cell to fire onSelectSlot.
 *   - Optional slot highlighting (Issue #226).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { ShelfMapSlot } from '@/services/shelfMapService';

// ---------------------------------------------------------------------------
// CSS keyframe animations (injected once into the document)
// ---------------------------------------------------------------------------

const SHELF_MAP_STYLE_ID = 'shelf-map-renderer-styles';

function ensureStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SHELF_MAP_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = SHELF_MAP_STYLE_ID;
  style.textContent = `
    /* ── Pick-up: magnetic-pull stretch then snap off ── */
    @keyframes smGhostLift {
      0%   { transform: scale(1, 1);    opacity: 0.7; box-shadow: 0 1px 4px rgba(0,0,0,.2); }
      30%  { transform: scale(1.08,.9); opacity: 0.85; box-shadow: 0 4px 10px rgba(0,0,0,.3); }
      65%  { transform: scale(.94,1.1); opacity: 1;   box-shadow: 0 16px 32px rgba(0,0,0,.5); }
      85%  { transform: scale(1.04,.97); opacity: 1;  box-shadow: 0 10px 24px rgba(0,0,0,.4); }
      100% { transform: scale(1, 1);    opacity: 1;   box-shadow: 0 8px 20px rgba(0,0,0,.38); }
    }
    /* ── In-flight: shimmer scan sweeping left-to-right ── */
    @keyframes smGhostShimmer {
      0%   { background-position: -200% center; }
      100% { background-position:  200% center; }
    }
    .sm-ghost-positioner {
      position: fixed;
      left: 0;
      top: 0;
      pointer-events: none;
      z-index: 9999;
      will-change: transform;
    }
    .sm-ghost-inner {
      animation:
        smGhostLift    0.22s cubic-bezier(0.34,1.56,0.64,1) forwards,
        smGhostShimmer 1.1s  linear 0.22s infinite;
      transform-origin: center bottom;
      will-change: transform, background-position;
    }
    /* ── Source slot: fast fade-out ── */
    .sm-slot-dragging {
      opacity: 0.15;
      transition: opacity 0.12s ease;
      transform-box: fill-box;
    }
    .sm-slot-g {
      transition: opacity 0.15s ease;
    }
    /* ── Drop-zone: ripple ring expanding from centre ── */
    @keyframes smDropRipple {
      0%   { stroke-width: 3;   stroke-opacity: 0.9; rx: 6;  }
      50%  { stroke-width: 5;   stroke-opacity: 0.6; rx: 10; }
      100% { stroke-width: 3;   stroke-opacity: 0.9; rx: 6;  }
    }
    @keyframes smDropGlow {
      0%,100% { opacity: 0.85; }
      50%     { opacity: 0.4;  }
    }
    .sm-drop-zone-pulse {
      animation: smDropGlow 0.55s ease-in-out infinite;
    }
    /* ── Land: stamp-in with ink-splash ring ── */
    @keyframes smSlotLand {
      0%   { transform: scale(1.45); opacity: 0;   filter: brightness(2) blur(2px); }
      35%  { transform: scale(0.88); opacity: 1;   filter: brightness(1.4) blur(0px); }
      60%  { transform: scale(1.06);               filter: brightness(1.1); }
      80%  { transform: scale(0.97);               filter: brightness(1); }
      100% { transform: scale(1);                  filter: brightness(1); }
    }
    .sm-slot-land {
      animation: smSlotLand 0.42s cubic-bezier(0.22,1,0.36,1) forwards;
      transform-box: fill-box;
      transform-origin: center;
    }
  `;
  document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// Layout constants
// ---------------------------------------------------------------------------

const CELL_W = 64; // slot cell width  (SVG units)
const CELL_H = 46; // slot cell height
const CELL_GAP = 6; // horizontal gap between cells
const SHELF_GAP = 8; // vertical gap between shelf sub-rows in an aisle
const AISLE_LABEL_W = 72; // fixed width of the aisle-label column
const AISLE_LABEL_GAP = 10; // gap between label column and first cell
const ROW_PAD_V = 10; // top/bottom padding inside each aisle row
const AISLE_GAP = 6; // vertical gap between aisle rows
const OUTER_PAD_H = 14; // left/right outer padding
const OUTER_PAD_V = 12; // top/bottom outer padding

// ---------------------------------------------------------------------------
// Theme-aware colours
// ---------------------------------------------------------------------------

function buildColors(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    cell: {
      default: isDark ? '#6b7280' : '#94a3b8',
      hover: isDark ? theme.palette.primary.dark + '50' : '#e3f2fd',
      selected: theme.palette.primary.main,
      // Slot with no linked location (unlinked)
      empty: isDark ? '#1c1e28' : '#fafafa',
      // Slot with inventory present: gray
      occupied: isDark ? '#374151' : '#e5e7eb',
      // Empty location (has location but no inventory): red
      noInventory: isDark ? '#991b1b' : '#fca5a5',
      // Search match: green
      highlighted: isDark ? '#14532d' : '#dcfce7',
    },
    cellBorder: {
      default: isDark ? '#9ca3af' : '#64748b',
      selected: theme.palette.primary.dark,
      occupied: isDark ? '#6b7280' : '#9ca3af',
      noInventory: isDark ? '#f87171' : '#dc2626',
      highlighted: isDark ? '#4ade80' : '#16a34a',
    },
    cellText: {
      default: isDark ? '#f9fafb' : '#111827',
      selected: theme.palette.primary.contrastText,
      muted: theme.palette.text.disabled,
    },
    rowBg: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)',
    rowBorder: theme.palette.divider,
    aisleLabel: isDark ? theme.palette.primary.light : theme.palette.primary.dark,
    svgBg: theme.palette.background.paper,
    svgBorder: theme.palette.divider,
  };
}

// ---------------------------------------------------------------------------
// Layout types
// ---------------------------------------------------------------------------

interface SlotEntry {
  slot: ShelfMapSlot;
  /** Absolute SVG x */
  x: number;
  /** Absolute SVG y */
  y: number;
}

interface AisleRow {
  label: string;
  /** Absolute SVG y for this row's top edge */
  rowY: number;
  /** Full height of this row (includes padding) */
  rowH: number;
  slots: SlotEntry[];
}

// ---------------------------------------------------------------------------
// Layout computation
// ---------------------------------------------------------------------------

function computeLayout(
  slots: ShelfMapSlot[],
  containerWidth: number,
): { rows: AisleRow[]; totalWidth: number; totalHeight: number } {
  if (slots.length === 0) return { rows: [], totalWidth: 0, totalHeight: 0 };

  // ── Group by aisle → shelf → sorted cells ─────────────────────────────
  const aisleMap = new Map<string, Map<number, ShelfMapSlot[]>>();
  for (const slot of slots) {
    let shelfMap = aisleMap.get(slot.aisle_label);
    if (!shelfMap) {
      shelfMap = new Map();
      aisleMap.set(slot.aisle_label, shelfMap);
    }
    const arr = shelfMap.get(slot.shelf_number) ?? [];
    arr.push(slot);
    shelfMap.set(slot.shelf_number, arr);
  }

  const sortedAisles = [...aisleMap.entries()].sort(([a], [b]) => a.localeCompare(b));

  // Cell area starts after the label column
  const cellsStartX = OUTER_PAD_H + AISLE_LABEL_W + AISLE_LABEL_GAP;

  const rows: AisleRow[] = [];
  let curY = OUTER_PAD_V;

  for (const [label, shelfMap] of sortedAisles) {
    const sortedShelves = [...shelfMap.entries()].sort(([a], [b]) => a - b);
    const slotEntries: SlotEntry[] = [];

    let relY = ROW_PAD_V; // y relative to row top

    for (const [, arr] of sortedShelves) {
      const sorted = [...arr].sort((a, b) => a.column_number - b.column_number);
      sorted.forEach((slot, ci) => {
        slotEntries.push({
          slot,
          x: cellsStartX + ci * (CELL_W + CELL_GAP),
          y: curY + relY,
        });
      });
      relY += CELL_H + SHELF_GAP;
    }

    const rowH = relY - SHELF_GAP + ROW_PAD_V;
    rows.push({ label, rowY: curY, rowH, slots: slotEntries });
    curY += rowH + AISLE_GAP;
  }

  // Total SVG width = widest needed to show all cells (or container width, whichever is larger)
  let maxSlotRight = cellsStartX;
  for (const row of rows) {
    for (const s of row.slots) {
      maxSlotRight = Math.max(maxSlotRight, s.x + CELL_W);
    }
  }
  const totalWidth = Math.max(containerWidth, maxSlotRight + OUTER_PAD_H);
  const totalHeight = curY - AISLE_GAP + OUTER_PAD_V;

  return { rows, totalWidth, totalHeight };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface ShelfMapRendererProps {
  slots: ShelfMapSlot[];
  selectedSlotId: string | null;
  onSelectSlot: (slotId: string, locationEntityId: string | null) => void;
  /** Slots that contain inventory → rendered in gray. */
  occupiedSlotIds?: Set<string>;
  highlightedSlotIds?: Set<string>;
  noInventorySlotIds?: Set<string>;
  /**
   * When provided, slots become draggable. Called when the user drops a slot
   * onto a different aisle row. The parent is responsible for persisting the
   * coordinate change via the API.
   *
   * @param slotId       The ID of the dragged slot.
   * @param targetAisle  The aisle label of the target row.
   * @param targetShelf  The shelf number within the target row closest to the drop point.
   */
  onMoveSlot?: (slotId: string, targetAisle: string, targetShelf: number) => void;
  className?: string;
  /**
   * When true, automatically scrolls/centers the viewport on `selectedSlotId`
   * once, on initial render (issue #1083). Only enabled by callers that open
   * the map from an asset's location context (Asset Detail View / Search) —
   * other launch points (e.g. catalog item detail) keep the default,
   * no-auto-scroll behavior.
   */
  autoScrollToSelection?: boolean;
}

const ZOOM_STEP = 0.25;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.0;

/** Pixels from the container edge that trigger auto-scroll. */
const SCROLL_ZONE = 60;
/** Maximum scroll speed in px per frame (~60 fps). */
const SCROLL_SPEED = 12;

// ---------------------------------------------------------------------------
// Drag state
// ---------------------------------------------------------------------------

interface DragState {
  slotId: string;
  sourceAisle: string;
  sourceShelf: number;
  ghostClientX: number;
  ghostClientY: number;
  targetAisle: string | null;
  targetShelf: number | null;
}

export const ShelfMapRenderer: React.FC<ShelfMapRendererProps> = ({
  slots,
  selectedSlotId,
  onSelectSlot,
  occupiedSlotIds,
  highlightedSlotIds,
  noInventorySlotIds,
  onMoveSlot,
  className,
  autoScrollToSelection,
}) => {
  const theme = useTheme();
  const colors = useMemo(() => buildColors(theme), [theme]);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasAutoScrolledRef = useRef(false);
  const [hoveredSlotId, setHoveredSlotId] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState(800);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState<0 | 90 | 180 | 270>(0);
  const [drag, setDrag] = useState<DragState | null>(null);
  // Keep a ref so window-level handlers always read the latest drag state
  const dragRef = useRef<DragState | null>(null);
  dragRef.current = drag;
  const onMoveSlotRef = useRef(onMoveSlot);
  onMoveSlotRef.current = onMoveSlot;
  // Ref that tracks live ghost position for the rAF scroll loop
  const ghostClientYRef = useRef<number>(0);
  const scrollRafRef = useRef<number | null>(null);
  // Track which slot just landed (for the land-bounce animation)
  const [justLandedSlotId, setJustLandedSlotId] = useState<string | null>(null);

  // Inject CSS keyframes once
  useEffect(() => {
    ensureStyles();
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setContainerWidth(el.clientWidth || 800);
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      if (w > 0) setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { rows, totalWidth, totalHeight } = useMemo(
    () => computeLayout(slots, containerWidth),
    [slots, containerWidth],
  );

  // Auto-scroll/center the viewport on the selected slot, once, when the map
  // is opened from a launch point that requests it (#1083). Runs only once
  // per mount so it doesn't fight the user's own panning/scrolling afterward.
  useEffect(() => {
    if (!autoScrollToSelection || hasAutoScrolledRef.current) return;
    if (!selectedSlotId) return;
    const container = containerRef.current;
    if (!container || rows.length === 0) return;

    let target: { x: number; y: number } | null = null;
    for (const row of rows) {
      const entry = row.slots.find((s) => s.slot.id === selectedSlotId);
      if (entry) {
        target = { x: entry.x + CELL_W / 2, y: entry.y + CELL_H / 2 };
        break;
      }
    }
    if (!target) return;

    hasAutoScrolledRef.current = true;
    container.scrollTo({
      left: Math.max(0, target.x * zoom - container.clientWidth / 2),
      top: Math.max(0, target.y * zoom - container.clientHeight / 2),
      behavior: 'auto',
    });
  }, [autoScrollToSelection, selectedSlotId, rows, zoom]);

  // ── Drag helpers ────────────────────────────────────────────────────────

  /** Convert browser client coordinates → SVG (unscaled) coordinates. */
  const clientToSvgCoords = useCallback(
    (clientX: number, clientY: number) => {
      const container = containerRef.current;
      if (!container) return { x: 0, y: 0 };
      const rect = container.getBoundingClientRect();
      const x = (clientX - rect.left + container.scrollLeft) / zoom;
      const y = (clientY - rect.top + container.scrollTop) / zoom;
      return { x, y };
    },
    [zoom],
  );

  /** Find the aisle row and shelf sub-row that contains the given SVG y position. */
  const findDropTarget = useCallback(
    (svgY: number): { aisle: string; shelf: number } | null => {
      for (const row of rows) {
        if (svgY >= row.rowY && svgY < row.rowY + row.rowH) {
          const shelfNumbers = [...new Set(row.slots.map((s) => s.slot.shelf_number))].sort(
            (a, b) => a - b,
          );
          const relY = svgY - row.rowY - ROW_PAD_V;
          let idx = Math.floor(relY / (CELL_H + SHELF_GAP));
          idx = Math.max(0, Math.min(idx, shelfNumbers.length - 1));
          return { aisle: row.label, shelf: shelfNumbers[idx] ?? 1 };
        }
      }
      return null;
    },
    [rows],
  );

  /** Start a drag on pointer-down over a slot cell. */
  const handleSlotPointerDown = useCallback(
    (e: React.PointerEvent, slot: ShelfMapSlot) => {
      if (!onMoveSlotRef.current) return;
      // Drag hit-testing uses unrotated SVG coordinates; disable while rotated
      // to prevent slots from being dropped on the wrong target.
      if (rotation !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      setDrag({
        slotId: slot.id,
        sourceAisle: slot.aisle_label,
        sourceShelf: slot.shelf_number,
        ghostClientX: e.clientX,
        ghostClientY: e.clientY,
        targetAisle: null,
        targetShelf: null,
      });
    },
    [rotation],
  );

  /** Window-level pointer handlers – attached only while dragging. */
  useEffect(() => {
    if (!drag) return;

    // ── Auto-scroll rAF loop ──────────────────────────────────────────────
    const runScroll = () => {
      const container = containerRef.current;
      if (container) {
        const rect = container.getBoundingClientRect();
        const y = ghostClientYRef.current;
        const distFromTop = y - rect.top;
        const distFromBottom = rect.bottom - y;

        if (distFromTop < SCROLL_ZONE && distFromTop > 0) {
          // Cursor near top edge – scroll up
          const speed = SCROLL_SPEED * (1 - distFromTop / SCROLL_ZONE);
          container.scrollTop = Math.max(0, container.scrollTop - speed);
        } else if (distFromBottom < SCROLL_ZONE && distFromBottom > 0) {
          // Cursor near bottom edge – scroll down
          const speed = SCROLL_SPEED * (1 - distFromBottom / SCROLL_ZONE);
          container.scrollTop = Math.min(
            container.scrollHeight - container.clientHeight,
            container.scrollTop + speed,
          );
        }
      }
      scrollRafRef.current = requestAnimationFrame(runScroll);
    };
    scrollRafRef.current = requestAnimationFrame(runScroll);

    const handleMove = (e: PointerEvent) => {
      ghostClientYRef.current = e.clientY;
      const coords = clientToSvgCoords(e.clientX, e.clientY);
      const target = findDropTarget(coords.y);
      setDrag((prev) => {
        if (!prev) return null;
        // Only re-render when ghost position changed meaningfully OR target row changed
        const targetChanged =
          target?.aisle !== prev.targetAisle || target?.shelf !== prev.targetShelf;
        const moved =
          Math.abs(e.clientX - prev.ghostClientX) > 1 ||
          Math.abs(e.clientY - prev.ghostClientY) > 1;
        if (!moved && !targetChanged) return prev; // bail – no state update
        return {
          ...prev,
          ghostClientX: e.clientX,
          ghostClientY: e.clientY,
          targetAisle: target?.aisle ?? null,
          targetShelf: target?.shelf ?? null,
        };
      });
    };

    const handleUp = () => {
      const d = dragRef.current;
      // Clear drag immediately so the ghost disappears and land frame renders first
      setDrag(null);
      const isDifferentRow =
        d &&
        d.targetAisle != null &&
        d.targetShelf != null &&
        (d.targetAisle !== d.sourceAisle || d.targetShelf !== d.sourceShelf);
      if (isDifferentRow) {
        // Apply land animation in this frame
        setJustLandedSlotId(d!.slotId);
        setTimeout(() => setJustLandedSlotId(null), 500);
        // Defer mutation by one rAF so the ghost-disappear + land frames render before
        // React Query invalidation triggers a re-render
        requestAnimationFrame(() => {
          onMoveSlotRef.current?.(d!.slotId, d!.targetAisle!, d!.targetShelf!);
        });
      }
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      if (scrollRafRef.current != null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag != null, clientToSvgCoords, findDropTarget]);

  const handleSlotClick = useCallback(
    (e: React.MouseEvent, slotId: string, locationEntityId: string | null) => {
      // Suppress click if it was the end of a drag gesture
      if (dragRef.current) return;
      e.stopPropagation();
      onSelectSlot(slotId, locationEntityId);
    },
    [onSelectSlot],
  );

  if (slots.length === 0) {
    return (
      <div
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 200,
          color: colors.cellText.muted,
          fontSize: 14,
        }}
      >
        No slots configured for this layout. Add aisles and shelves in the configuration panel.
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Scrollable map area */}
      <div
        ref={containerRef}
        className={className}
        style={{
          flex: 1,
          overflow: 'auto',
          background: colors.svgBg,
          border: `1px solid ${colors.svgBorder}`,
          borderRadius: 6,
          cursor: drag ? 'grabbing' : 'default',
          userSelect: drag ? 'none' : undefined,
          touchAction: drag ? 'none' : undefined,
        }}
      >
        {/* Wrapper div sized to the SVG's post-rotation footprint so the scroll
            container gets correct scroll extents. When rotated 90°/270°, width
            and height are swapped; the SVG is absolutely positioned and centred
            inside the wrapper so the CSS rotation aligns with the scroll area. */}
        {(() => {
          const naturalW = Math.max(totalWidth, containerWidth) * zoom;
          const naturalH = totalHeight * zoom;
          const isTransposed = rotation === 90 || rotation === 270;
          const displayW = isTransposed ? naturalH : naturalW;
          const displayH = isTransposed ? naturalW : naturalH;
          const offsetX = (displayW - naturalW) / 2;
          const offsetY = (displayH - naturalH) / 2;
          return (
            <div style={{ position: 'relative', width: displayW, height: displayH, flexShrink: 0 }}>
              <svg
                width={naturalW}
                height={naturalH}
                display="block"
                aria-label="Section shelf map"
                style={{
                  position: 'absolute',
                  left: offsetX,
                  top: offsetY,
                  transformOrigin: '50% 50%',
                  transform: rotation !== 0 ? `rotate(${rotation}deg)` : undefined,
                  transition: 'transform 0.25s ease',
                }}
              >
                {/* Scale group */}
                <g transform={`scale(${zoom})`}>
                  {rows.map((row, ri) => (
                    <g key={`row-${row.label}`}>
                      {/* Row background band – highlighted + pulsing when a drag is hovering over it */}
                      {(() => {
                        const isDropTarget = drag?.targetAisle === row.label;
                        return (
                          <rect
                            x={OUTER_PAD_H}
                            y={row.rowY}
                            width={Math.max(totalWidth, containerWidth) - OUTER_PAD_H * 2}
                            height={row.rowH}
                            rx={6}
                            className={isDropTarget ? 'sm-drop-zone-pulse' : undefined}
                            fill={
                              isDropTarget
                                ? colors.cell.hover
                                : ri % 2 === 0
                                  ? colors.rowBg
                                  : 'transparent'
                            }
                            stroke={isDropTarget ? colors.cellBorder.selected : colors.rowBorder}
                            strokeWidth={isDropTarget ? 2.5 : 0.5}
                          />
                        );
                      })()}

                      {/* Aisle label – truncated with ellipsis; <title> provides native tooltip on hover */}
                      {(() => {
                        // Estimate ~7px per character at font-size 11. Truncate if needed.
                        const maxChars = Math.floor(AISLE_LABEL_W / 7);
                        const truncated =
                          row.label.length > maxChars
                            ? row.label.slice(0, maxChars - 1) + '…'
                            : row.label;
                        return (
                          <text
                            x={OUTER_PAD_H + AISLE_LABEL_W / 2}
                            y={row.rowY + row.rowH / 2}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize={11}
                            fontWeight="700"
                            fill={colors.aisleLabel}
                          >
                            <title>{row.label}</title>
                            {truncated}
                          </text>
                        );
                      })()}

                      {/* Slot cells */}
                      {row.slots.map((entry) => {
                        const { slot, x, y } = entry;
                        const isSelected = slot.id === selectedSlotId;
                        const isHovered = slot.id === hoveredSlotId;
                        const isHighlighted =
                          (highlightedSlotIds?.size ?? 0) > 0 &&
                          (highlightedSlotIds?.has(slot.id) ?? false);
                        const isOccupied =
                          !isHighlighted &&
                          (occupiedSlotIds?.size ?? 0) > 0 &&
                          (occupiedSlotIds?.has(slot.id) ?? false);
                        const isNoInventory =
                          !isHighlighted &&
                          !isOccupied &&
                          (noInventorySlotIds?.size ?? 0) > 0 &&
                          (noInventorySlotIds?.has(slot.id) ?? false);
                        const hasLocation = !!slot.location_entity_id;
                        // Prefer the most specific location name (Issue #1081) — the full
                        // breadcrumb starts with the top-level ancestor, so truncating it to
                        // fit the cell width ends up showing only that top-level name.
                        const displayLabel =
                          slot.label_override ??
                          slot.location_name ??
                          slot.location_breadcrumb ??
                          (hasLocation ? '…' : null);
                        // Truncate with an ellipsis (matching the aisle-label truncation
                        // above) instead of cutting mid-word with no visual indicator.
                        const maxSlotLabelChars = Math.floor(CELL_W / 7);
                        const truncatedDisplayLabel =
                          displayLabel && displayLabel.length > maxSlotLabelChars
                            ? displayLabel.slice(0, maxSlotLabelChars - 1) + '…'
                            : displayLabel;

                        return (
                          <g
                            key={slot.id}
                            role="button"
                            tabIndex={0}
                            aria-label={
                              slot.location_breadcrumb ??
                              `${row.label} / ${displayLabel ?? slot.column_number}`
                            }
                            aria-pressed={isSelected}
                            className={
                              justLandedSlotId === slot.id
                                ? 'sm-slot-land'
                                : drag?.slotId === slot.id
                                  ? 'sm-slot-dragging'
                                  : 'sm-slot-g'
                            }
                            style={{
                              cursor:
                                onMoveSlot && rotation === 0
                                  ? drag?.slotId === slot.id
                                    ? 'grabbing'
                                    : 'grab'
                                  : 'pointer',
                              pointerEvents: 'all',
                            }}
                            onClick={(e) => handleSlotClick(e, slot.id, slot.location_entity_id)}
                            onPointerDown={(e) => handleSlotPointerDown(e, slot)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onSelectSlot(slot.id, slot.location_entity_id);
                              }
                            }}
                            onMouseEnter={() => !drag && setHoveredSlotId(slot.id)}
                            onMouseLeave={() => setHoveredSlotId(null)}
                          >
                            <rect
                              x={x}
                              y={y}
                              width={CELL_W}
                              height={CELL_H}
                              rx={5}
                              fill={
                                isSelected
                                  ? colors.cell.selected
                                  : isHighlighted
                                    ? colors.cell.highlighted
                                    : isOccupied
                                      ? colors.cell.occupied
                                      : isNoInventory
                                        ? colors.cell.noInventory
                                        : isHovered
                                          ? colors.cell.hover
                                          : hasLocation
                                            ? colors.cell.default
                                            : colors.cell.empty
                              }
                              stroke={
                                isSelected
                                  ? colors.cellBorder.selected
                                  : isHighlighted
                                    ? colors.cellBorder.highlighted
                                    : isOccupied
                                      ? colors.cellBorder.occupied
                                      : isNoInventory
                                        ? colors.cellBorder.noInventory
                                        : colors.cellBorder.default
                              }
                              strokeWidth={
                                isSelected ? 2.5 : isHighlighted ? 2 : isOccupied ? 1.5 : 1
                              }
                            />
                            <text
                              x={x + CELL_W / 2}
                              y={y + CELL_H / 2}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              fontSize={11}
                              fontWeight={isSelected ? '700' : '500'}
                              fill={
                                isSelected
                                  ? colors.cellText.selected
                                  : isNoInventory
                                    ? colors.cellText.muted
                                    : displayLabel
                                      ? colors.cellText.default
                                      : colors.cellText.muted
                              }
                            >
                              {truncatedDisplayLabel ?? slot.column_number}
                            </text>
                            {/* SVG title provides native tooltip showing full breadcrumb path (#459);
                                only render it when the breadcrumb adds context beyond the specific
                                name shown in the cell, to avoid a duplicate tooltip like
                                "storefront, storefront" for top-level/root locations (#1081). */}
                            {slot.location_breadcrumb &&
                              slot.location_breadcrumb !== slot.location_name && (
                                <title>{slot.location_breadcrumb}</title>
                              )}
                          </g>
                        );
                      })}
                    </g>
                  ))}
                </g>
              </svg>
            </div>
          );
        })()}
      </div>

      {/* Drag ghost – follows the cursor as a fixed-position overlay */}
      {drag &&
        (() => {
          const gs = slots.find((s) => s.id === drag.slotId);
          const label =
            gs?.label_override ??
            gs?.location_name ??
            gs?.location_breadcrumb ??
            String(gs?.column_number ?? '');
          return (
            <div
              aria-hidden
              className="sm-ghost-positioner"
              style={{
                transform: `translate(${drag.ghostClientX - CELL_W / 2}px, ${drag.ghostClientY - CELL_H / 2}px)`,
              }}
            >
              <div
                className="sm-ghost-inner"
                style={{
                  width: CELL_W,
                  height: CELL_H,
                  backgroundColor: colors.cell.selected,
                  border: `2px solid ${colors.cellBorder.selected}`,
                  borderRadius: 5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.cellText.selected,
                  userSelect: 'none',
                  backgroundImage: `linear-gradient(105deg, ${colors.cell.selected} 40%, rgba(255,255,255,0.5) 50%, ${colors.cell.selected} 60%)`,
                  backgroundSize: '200% 100%',
                }}
              >
                {label.length > 10 ? label.slice(0, 10) : label}
              </div>
            </div>
          );
        })()}

      {/* Toolbar: rotation + zoom */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '4px 10px',
          borderTop: `1px solid ${colors.svgBorder}`,
          background: colors.svgBg,
          borderRadius: '0 0 6px 6px',
          userSelect: 'none',
        }}
      >
        {/* Rotation toggle */}
        <button
          aria-label="Rotate map 90°"
          title="Rotate map 90°"
          onClick={() =>
            setRotation((r) => {
              const next = (r + 90) % 360;
              return next as 0 | 90 | 180 | 270;
            })
          }
          style={{
            cursor: 'pointer',
            background: rotation !== 0 ? colors.cell.hover : 'none',
            border: `1px solid ${colors.svgBorder}`,
            borderRadius: 4,
            width: 28,
            height: 22,
            fontSize: 13,
            lineHeight: '20px',
            textAlign: 'center',
            padding: 0,
            color: colors.cellText.default,
          }}
        >
          ↻
        </button>
        {rotation !== 0 && (
          <span style={{ fontSize: 11, color: colors.cellText.muted }}>{rotation}°</span>
        )}
        <span
          style={{
            width: 1,
            height: 16,
            background: colors.svgBorder,
            margin: '0 4px',
          }}
        />
        <span style={{ fontSize: 12, color: colors.cellText.muted, marginRight: 2 }}>Zoom</span>
        <button
          aria-label="Zoom out"
          onClick={() => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))}
          disabled={zoom <= ZOOM_MIN}
          style={{
            cursor: zoom <= ZOOM_MIN ? 'default' : 'pointer',
            background: 'none',
            border: `1px solid ${colors.svgBorder}`,
            borderRadius: 4,
            width: 22,
            height: 22,
            fontSize: 14,
            lineHeight: '20px',
            textAlign: 'center',
            padding: 0,
            color: zoom <= ZOOM_MIN ? colors.cellText.muted : colors.cellText.default,
          }}
        >
          −
        </button>
        <span
          style={{
            fontSize: 12,
            minWidth: 38,
            textAlign: 'center',
            color: colors.cellText.default,
          }}
        >
          {Math.round(zoom * 100)}%
        </span>
        <button
          aria-label="Zoom in"
          onClick={() => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))}
          disabled={zoom >= ZOOM_MAX}
          style={{
            cursor: zoom >= ZOOM_MAX ? 'default' : 'pointer',
            background: 'none',
            border: `1px solid ${colors.svgBorder}`,
            borderRadius: 4,
            width: 22,
            height: 22,
            fontSize: 14,
            lineHeight: '20px',
            textAlign: 'center',
            padding: 0,
            color: zoom >= ZOOM_MAX ? colors.cellText.muted : colors.cellText.default,
          }}
        >
          +
        </button>
      </div>
    </div>
  );
};
